import { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import api from '../../../../api';
import * as DataListAPI from '../../../../api/dataList';
import { pickSelectedId, printPdf } from '../../../../utils/funcoes';
import { useActionLock } from '../../../../hooks/useActionLock';
import useFlashMessage from '../../../../hooks/useFlashMessage';

const novaChapa = () => ({ numero: '', nome: '', grupo: 'diretoria', dataInscricao: moment().format('YYYY-MM-DD'), observacao: '', membros: [] });
const novoMembro = (ordem = 1) => ({ pessoaId: null, cargoId: null, unidadeCondominialId: null, pessoa: '', cargo: '', unidade: '', necessitaAutorizacao: false, ordem });
const proximaOrdem = (membros) => Math.max(0, ...membros.map((m) => Number(m.ordem) || 0)) + 1;
const mensagem = (e) => e.response?.data?.message || e.message || 'Não foi possível realizar a operação.';

export function useChapasModule({ eleicao, permissoes, bloqueado }) {
    const [referenciaAutorizacao, setReferenciaAutorizacao] = useState('');
    const [lista, setLista] = useState([]), [item, setItem] = useState(novaChapa);
    const [membro, setMembro] = useState(novoMembro), [editandoMembro, setEditandoMembro] = useState(null);
    const [opcoes, setOpcoes] = useState({ pessoa: [], cargo: [], unidade: [] });
    const [sujo, setSujo] = useState(false), [carregando, setCarregando] = useState(false), [erro, setErro] = useState('');
    const contexto = useRef(0), busca = useRef({ pessoa: 0, cargo: 0, unidade: 0 }), listaVersao = useRef(0);
    const { isLocked, run } = useActionLock();
    const { setFlashMessage } = useFlashMessage();
    const base = `/eleicao/${eleicao.uuid}/chapas`;
    const ocupado = bloqueado || isLocked || carregando;
    const podeAlterar = permissoes.autorizado && (item.uuid ? permissoes.editar : permissoes.incluir);
    const membroPendente = editandoMembro !== null || Boolean(membro.pessoa || membro.cargo || membro.unidade || membro.necessitaAutorizacao);
    const pendente = sujo || membroPendente;
    const invalidarBuscas = () => { Object.keys(busca.current).forEach((k) => { busca.current[k] += 1; }); };
    const limparMembro = (membros = item.membros) => {
        invalidarBuscas(); setOpcoes({ pessoa: [], cargo: [], unidade: [] });
        setMembro(novoMembro(proximaOrdem(membros))); setEditandoMembro(null);
    };
    const limparChapa = () => { setItem(novaChapa()); setSujo(false); limparMembro([]); };
    const carregar = async () => {
        if (!eleicao.uuid || !permissoes.autorizado) return;
        const atual = ++listaVersao.current, ctx = contexto.current;
        setCarregando(true); setErro('');
        try {
            const { data } = await api.get(base);
            if (!Array.isArray(data)) throw new Error('Resposta inválida ao listar chapas.');
            if (ctx === contexto.current && atual === listaVersao.current) setLista(data);
        } catch (e) {
            if (ctx === contexto.current && atual === listaVersao.current) setErro(mensagem(e));
        } finally {
            if (ctx === contexto.current && atual === listaVersao.current) setCarregando(false);
        }
    };
    useEffect(() => {
        contexto.current += 1; listaVersao.current += 1;
        setLista([]); setErro(''); setCarregando(false); limparChapa(); setReferenciaAutorizacao('');
        if (eleicao.uuid && permissoes.autorizado) carregar();
        return () => { contexto.current += 1; listaVersao.current += 1; invalidarBuscas(); };
        // O estado e as buscas pertencem à eleição aberta.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eleicao.uuid, eleicao.empresaId, permissoes.autorizado]);
    const avisar = (texto) => setFlashMessage(texto, 'warning');
    const alterar = ({ target: { name, value } }) => {
        if (ocupado || !podeAlterar) return;
        if (name === 'grupo' && value !== item.grupo && (item.membros.length || membroPendente)) {
            avisar('Remova os membros e limpe a edição de membro antes de trocar o grupo.'); return;
        }
        if (name === 'grupo') { invalidarBuscas(); setOpcoes({ pessoa: [], cargo: [], unidade: [] }); }
        setItem((prev) => ({ ...prev, [name]: value })); setSujo(true);
    };
    const consultar = async (campo, texto) => {
        const atual = ++busca.current[campo], ctx = contexto.current;
        setOpcoes((prev) => ({ ...prev, [campo]: [] }));
        if (!eleicao.empresaId || texto.trim().length < 2) return;
        try {
            const data = await DataListAPI.dataList({ empresaId: eleicao.empresaId,
                tabela: { pessoa: 'Pessoa', cargo: 'rhCargo', unidade: 'UnidadeCondominial' }[campo],
                condicao: item.grupo === 'fiscal' ? 'eleicaoFiscal' : 'eleicaoDiretoria', filtro: texto });
            if (atual !== busca.current[campo] || ctx !== contexto.current) return;
            if (!Array.isArray(data)) throw new Error('Não foi possível consultar os registros.');
            setOpcoes((prev) => ({ ...prev, [campo]: data.map((o) => ({ ...o, label: campo === 'cargo' ? '' : o.label || '' })) }));
        } catch (e) { if (atual === busca.current[campo] && ctx === contexto.current) setFlashMessage(mensagem(e), 'error'); }
    };
    const alterarMembro = ({ target: { name, value, checked } }) => {
        if (ocupado || !podeAlterar) return;
        if (name === 'necessitaAutorizacao') { setMembro((prev) => ({ ...prev, [name]: checked })); return; }
        if (name === 'ordem') { setMembro((prev) => ({ ...prev, ordem: value })); return; }
        if (!['pessoa', 'cargo', 'unidade'].includes(name)) return;
        const options = opcoes[name];
        const id = pickSelectedId(value, options);
        const escolhido = options.find((o) => Number(o.id) === Number(id));
        const key = name === 'unidade' ? 'unidadeCondominialId' : `${name}Id`;
        setMembro((prev) => ({ ...prev, [name]: escolhido?.descricao || value, [key]: id || null }));
        if (id) busca.current[name] += 1;
        else consultar(name, value);
    };
    const adicionarMembro = () => {
        if (ocupado || !podeAlterar) return;
        if (!membro.pessoaId || !membro.cargoId || !Number.isInteger(Number(membro.ordem)) || Number(membro.ordem) < 1) {
            avisar('Selecione pessoa, cargo e uma ordem válida.'); return;
        }
        if ((membro.unidade || membro.necessitaAutorizacao) && !membro.unidadeCondominialId) { avisar('Selecione uma unidade válida.'); return; }
        const pessoaId = Number(membro.pessoaId);
        if (item.membros.some((m) => Number(m.pessoaId) === pessoaId && Number(m.pessoaId) !== editandoMembro)) {
            avisar('Essa pessoa já ocupa um cargo nesta chapa.'); return;
        }
        if (lista.some((chapa) => chapa.uuid !== item.uuid && chapa.membros?.some((m) => Number(m.pessoaId) === pessoaId))) {
            avisar('Essa pessoa já ocupa um cargo em outra chapa desta eleição.'); return;
        }
        const pessoa = opcoes.pessoa.find((o) => Number(o.id) === pessoaId);
        const unidade = opcoes.unidade.find((o) => Number(o.id) === Number(membro.unidadeCondominialId));
        const novo = { pessoaId, cargoId: Number(membro.cargoId), ordem: Number(membro.ordem),
            necessitaAutorizacao: membro.necessitaAutorizacao, unidadeCondominialId: membro.unidadeCondominialId ? Number(membro.unidadeCondominialId) : null,
            Pessoa: { descricao: membro.pessoa, label: pessoa?.label || '' }, rhCargo: { descricao: membro.cargo },
            UnidadeCondominial: membro.unidadeCondominialId ? { descricao: membro.unidade, label: unidade?.label || '' } : null,
            Autorizante: null, autorizantePessoaId: null };
        const membros = editandoMembro === null ? [...item.membros, novo]
            : item.membros.map((m) => Number(m.pessoaId) === editandoMembro ? { ...m, ...novo } : m);
        setItem((prev) => ({ ...prev, membros })); setSujo(true); limparMembro(membros);
    };
    const editarMembro = (row) => {
        if (ocupado || !podeAlterar) return;
        if (membroPendente) { avisar('Conclua ou limpe a edição de membro atual.'); return; }
        invalidarBuscas();
        setOpcoes({ pessoa: [{ id: row.pessoaId, descricao: row.Pessoa?.descricao || '', label: row.Pessoa?.label || row.Pessoa?.CPForCNPJ || '' }],
            cargo: [{ id: row.cargoId, descricao: row.rhCargo?.descricao || '', label: '' }],
            unidade: row.unidadeCondominialId ? [{ id: row.unidadeCondominialId, descricao: row.UnidadeCondominial?.descricao || '', label: row.UnidadeCondominial?.label || row.UnidadeCondominial?.codigo || '' }] : [] });
        setMembro({ pessoaId: row.pessoaId, cargoId: row.cargoId, unidadeCondominialId: row.unidadeCondominialId,
            pessoa: row.Pessoa?.descricao || '', cargo: row.rhCargo?.descricao || '', unidade: row.UnidadeCondominial?.descricao || '',
            ordem: row.ordem, necessitaAutorizacao: Boolean(row.necessitaAutorizacao) });
        setEditandoMembro(Number(row.pessoaId));
    };
    const removerMembro = (row) => {
        if (ocupado || !podeAlterar) return;
        if (membroPendente) { avisar('Conclua ou limpe a edição de membro atual.'); return; }
        const membros = item.membros.filter((m) => Number(m.pessoaId) !== Number(row.pessoaId));
        setItem((prev) => ({ ...prev, membros })); setSujo(true); limparMembro(membros);
    };
    const selecionar = (row) => {
        if (ocupado) return;
        if (pendente) { avisar('Salve ou descarte a edição da chapa atual.'); return; }
        setItem({ ...row, membros: row.membros || [] }); setSujo(false); limparMembro(row.membros || []);
    };
    const novo = () => {
        if (ocupado) return;
        if (pendente && !window.confirm('Descartar as alterações não salvas desta chapa?')) return;
        limparChapa();
    };
    const salvar = () => {
        if (ocupado || !podeAlterar || !eleicao.uuid || erro) return;
        if (membroPendente) { avisar('Adicione/atualize o membro ou limpe sua edição antes de salvar a chapa.'); return; }
        const ctx = contexto.current;
        return run(async () => {
            try {
                if (!Number.isInteger(Number(item.numero)) || Number(item.numero) < 1 || !item.nome.trim()) throw new Error('Informe número e nome da chapa.');
                if (!moment(item.dataInscricao, 'YYYY-MM-DD', true).isValid()) throw new Error('Informe uma data de inscrição válida.');
                if (lista.some((c) => c.uuid !== item.uuid && c.grupo === item.grupo && Number(c.numero) === Number(item.numero))) throw new Error('Número de chapa já cadastrado neste grupo.');
                const payload = { numero: Number(item.numero), nome: item.nome.trim(), grupo: item.grupo, dataInscricao: item.dataInscricao,
                    observacao: item.observacao || null, membros: item.membros.map(({ pessoaId, cargoId, ordem, unidadeCondominialId, necessitaAutorizacao }) =>
                        ({ pessoaId, cargoId, ordem, unidadeCondominialId, necessitaAutorizacao })) };
                const { data } = await api.post(item.uuid ? `${base}/${item.uuid}/editar` : base, payload);
                if (ctx !== contexto.current) return;
                if (data.type && data.type !== 'success') throw new Error(data.message || 'Chapa não salva.');
                limparChapa(); setFlashMessage(data.message || 'Chapa salva com sucesso!', 'success'); await carregar();
            } catch (e) { if (ctx === contexto.current) setFlashMessage(mensagem(e), 'error'); }
        });
    };
    const excluir = () => {
        if (ocupado || pendente || !item.uuid || !permissoes.cancelar) return;
        if (!window.confirm(`Excluir a chapa ${item.numero} — ${item.nome} e seus membros?`)) return;
        const ctx = contexto.current;
        return run(async () => {
            try {
                const { data } = await api.post(`${base}/${item.uuid}/excluir`);
                if (ctx !== contexto.current) return;
                if (data.type && data.type !== 'success') throw new Error(data.message || 'Chapa não excluída.');
                limparChapa(); setFlashMessage(data.message, 'success'); await carregar();
            } catch (e) { if (ctx === contexto.current) setFlashMessage(mensagem(e), 'error'); }
        });
    };
    const imprimir = (somenteSelecionada = false, tipo = 'resumo') => {
        const caminho = { resumo: 'imprimir', qualificacao: 'qualificacao', autorizacoes: 'autorizacoes' }[tipo];
        if (!['resumo', 'qualificacao', 'autorizacoes'].includes(tipo)) return;
        if (ocupado || !eleicao.uuid || !permissoes.autorizado || !permissoes.imprimir) return;
        if (pendente) { avisar('Salve ou descarte a edição da chapa antes de imprimir.'); return; }
        if (somenteSelecionada && !item.uuid) { avisar('Selecione uma chapa para imprimir.'); return; }
        const ctx = contexto.current;
        return run(async () => {
            try {
                const url = somenteSelecionada ? `${base}/${item.uuid}/${caminho}` : `${base}/${caminho}`;
                const { data } = await api.post(url, tipo === 'autorizacoes' ? { referenciaAutorizacao } : {}, { responseType: 'arraybuffer' });
                if (ctx === contexto.current) printPdf(data);
            } catch (e) {
                if (ctx !== contexto.current) return;
                // O Axios mantém o erro JSON em arraybuffer nesta requisição de PDF.
                let texto = mensagem(e);
                if (e.response?.data instanceof ArrayBuffer || ArrayBuffer.isView(e.response?.data)) {
                    try { texto = JSON.parse(new TextDecoder().decode(e.response.data)).message || texto; } catch { /* mantém a mensagem original */ }
                }
                setFlashMessage(texto, 'error');
            }
        });
    };
    return { lista, item, membro, opcoes, sujo, pendente, membroPendente, editandoMembro: editandoMembro !== null,
        referenciaAutorizacao, alterarReferencia: (e) => { if (!ocupado && permissoes.imprimir) setReferenciaAutorizacao(e.target.value); },
        carregando, erro, ocupado, podeAlterar, alterar, alterarMembro, adicionarMembro, editarMembro, removerMembro,
        limparMembro: () => { if (!ocupado) limparMembro(); }, novo, selecionar, salvar, excluir, imprimir,
        carregar: () => { if (!ocupado) return carregar(); } };
}
